import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { Search, Pin, Archive, Copy, Trash2, RotateCcw, Play, Pause, Lock, BarChart3, Send } from "lucide-react";

const STATUS_ICON = { Published: Play, Paused: Pause, Private: Lock };

export default function AppManager() {
  const nav = useNavigate();
  const confirm = useConfirm();
  const [apps, setApps] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState({});
  const [summary, setSummary] = useState([]);
  const [transfers, setTransfers] = useState([]);

  const load = useCallback(() => {
    api.get(`/builder/apps`, { params: { q, status, include_archived: showArchived } })
      .then((r) => setApps(r.data.apps || [])).catch((e) => toast.error(errMsg(e)));
  }, [q, status, showArchived]);
  useEffect(load, [load]);
  useEffect(() => {
    api.get(`/builder/apps/credit-summary`).then((r) => setSummary(r.data.apps || [])).catch(() => {});
    api.get(`/builder/apps/transfers/pending`).then((r) => setTransfers(r.data.transfers || [])).catch(() => {});
  }, []);

  const act = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const togglePin = async (a) => {
    const pinned = apps.filter((x) => x.pinned).map((x) => x.id);
    const next = a.pinned ? pinned.filter((id) => id !== a.id) : [...pinned, a.id];
    act(() => api.post(`/builder/apps/prefs`, { pinned: next }));
  };
  const duplicate = async (a) => {
    const ok = await confirm({ title: `Duplicate "${a.name}"?`, body: "Duplication is effectively a NEW build and carries its own real credit cost from here on — it is not a free copy.", confirmLabel: "Duplicate (costed)" });
    if (!ok) return;
    act(async () => { const { data } = await api.post(`/builder/apps/${a.id}/duplicate`); toast.info(data.cost_note); });
  };
  const del = async (a) => {
    const ok = await confirm({ title: `Delete "${a.name}"?`, body: "This schedules deletion with a 24-hour grace period. You can undo any time before then — nothing is removed during the window.", confirmLabel: "Schedule deletion" });
    if (!ok) return;
    act(() => api.post(`/builder/apps/${a.id}/delete`), "Scheduled — undo available for 24h");
  };
  const bulk = async (action) => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return toast.error("Select apps first");
    act(async () => { await api.post(`/builder/apps/bulk`, { ids, action }); setSelected({}); }, `${action} applied to ${ids.length}`);
  };
  const acceptTransfer = async (t) => act(async () => { await api.post(`/builder/apps/transfers/${t.id}/accept`); setTransfers((x) => x.filter((y) => y.id !== t.id)); }, "Ownership accepted");
  const note = async (a) => { const n = window.prompt("Short note for this app:", a.note || ""); if (n === null) return; act(() => api.post(`/builder/apps/${a.id}/note`, { note: n })); };

  return (
    <Shell active="workspace">
      <h1 className="k-heading" style={{ fontSize: 26, marginTop: 0 }}>Your apps</h1>

      {transfers.length > 0 && transfers.map((t) => (
        <div key={t.id} data-testid={`transfer-${t.id}`} className="k-container" style={{ padding: 12, marginBottom: 10, borderLeft: "3px solid var(--accent-primary)" }}>
          <span style={{ fontSize: 13 }}>You've been offered ownership of <strong>{t.project_name}</strong>. </span>
          <button className="k-btn k-btn-primary" data-testid={`transfer-accept-${t.id}`} onClick={() => acceptTransfer(t)} style={{ padding: "4px 10px" }}><Send size={12} /> Accept</button>
        </div>
      ))}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <span className="k-control" style={{ display: "inline-flex", alignItems: "center", gap: 6, maxWidth: 260 }}>
          <Search size={14} /><input data-testid="apps-search" placeholder="Search apps or notes…" value={q} onChange={(e) => setQ(e.target.value)} style={{ border: 0, background: "transparent", outline: "none", flex: 1, color: "inherit" }} />
        </span>
        <select className="k-control" data-testid="apps-filter-status" style={{ width: "auto" }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>{["Draft", "Published", "Paused", "Private"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="k-muted" style={{ fontSize: 13, display: "flex", gap: 4, alignItems: "center" }}>
          <input type="checkbox" data-testid="apps-show-archived" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived
        </label>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button className="k-btn k-btn-ghost" data-testid="bulk-pause" onClick={() => bulk("pause")}>Pause selected</button>
          <button className="k-btn k-btn-ghost" data-testid="bulk-archive" onClick={() => bulk("archive")}>Archive selected</button>
        </div>
      </div>

      {apps.length === 0 ? (
        <div data-testid="apps-empty" className="k-container" style={{ padding: 40, textAlign: "center" }}>
          <h3 className="k-heading" style={{ fontSize: 18 }}>Nothing built yet</h3>
          <p className="k-muted" style={{ fontSize: 14 }}>Welcome to Kadana. Describe an app in plain English and we'll help you build compliance-ready software.</p>
          <button className="k-btn k-btn-primary" data-testid="apps-empty-create" onClick={() => nav("/workspace/build")}>Start your first app</button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {apps.map((a) => {
            const SI = STATUS_ICON[a.status];
            return (
              <div key={a.id} data-testid={`app-card-${a.id}`} className="k-container" style={{ padding: 14, opacity: a.pending_delete ? 0.6 : 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" data-testid={`app-select-${a.id}`} checked={!!selected[a.id]} onChange={(e) => setSelected((s) => ({ ...s, [a.id]: e.target.checked }))} />
                  <strong style={{ flex: 1, cursor: "pointer" }} onClick={() => nav(`/workspace/project/${a.id}`)}>{a.name}</strong>
                  <button className="k-btn k-btn-ghost" data-testid={`app-pin-${a.id}`} onClick={() => togglePin(a)} title={a.pinned ? "Unpin" : "Pin"} style={{ padding: 4, color: a.pinned ? "var(--accent-primary)" : undefined }}><Pin size={13} /></button>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", margin: "6px 0", flexWrap: "wrap" }}>
                  <span className="k-badge" data-testid={`app-status-${a.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>{SI && <SI size={11} />}{a.status}</span>
                  {a.archived && <span className="k-badge">archived</span>}
                  {a.pending_delete && <span className="k-tag-red" style={{ fontSize: 11 }}>deleting — undo available</span>}
                </div>
                {a.note && <p className="k-muted" style={{ fontSize: 12, margin: "4px 0" }}>{a.note}</p>}
                <p className="k-muted" style={{ fontSize: 11, margin: "4px 0" }}>Created {String(a.created_at).slice(0, 10)} · modified {String(a.updated_at).slice(0, 10)} · {Number(a.credits_used).toLocaleString("en-GB")} credits</p>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                  <button className="k-btn k-btn-ghost" data-testid={`app-note-${a.id}`} onClick={() => note(a)} style={{ padding: "3px 8px", fontSize: 12 }}>Note</button>
                  <button className="k-btn k-btn-ghost" data-testid={`app-duplicate-${a.id}`} onClick={() => duplicate(a)} style={{ padding: "3px 8px", fontSize: 12 }}><Copy size={12} /> Duplicate</button>
                  <button className="k-btn k-btn-ghost" data-testid={`app-archive-${a.id}`} onClick={() => act(() => api.post(`/builder/apps/${a.id}/archive`, { archived: !a.archived }))} style={{ padding: "3px 8px", fontSize: 12 }}><Archive size={12} /> {a.archived ? "Unarchive" : "Archive"}</button>
                  {a.pending_delete
                    ? <button className="k-btn k-btn-ghost" data-testid={`app-undo-${a.id}`} onClick={() => act(() => api.post(`/builder/apps/${a.id}/undo-delete`), "Restored")} style={{ padding: "3px 8px", fontSize: 12 }}><RotateCcw size={12} /> Undo</button>
                    : <button className="k-btn k-btn-ghost" data-testid={`app-delete-${a.id}`} onClick={() => del(a)} style={{ padding: "3px 8px", fontSize: 12 }}><Trash2 size={12} /> Delete</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="k-container" data-testid="credit-summary" style={{ padding: 16, marginTop: 16 }}>
        <h3 className="k-heading" style={{ fontSize: 15, marginTop: 0, display: "flex", gap: 6, alignItems: "center" }}><BarChart3 size={15} /> Credit usage across your apps</h3>
        {summary.slice(0, 8).map((s) => (
          <div key={s.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0", borderTop: "1px solid var(--control)" }}>
            <span>{s.name}</span><strong>{Number(s.credits_used).toLocaleString("en-GB")} credits</strong>
          </div>
        ))}
      </div>
    </Shell>
  );
}
